<template>
<div class="world-page">
  <header-nav></header-nav>

  <div class="world">

  <!-- 粒子背景 canvas -->
  <canvas ref="bgCanvas" class="world-canvas"></canvas>

  <!-- 氛围光斑 -->
  <div class="ambient-orb orb1"></div>
  <div class="ambient-orb orb2"></div>
  <div class="ambient-orb orb3"></div>

  <!-- ① 开场：巨大"世界之窗"艺术文字（高低错落淡入 → 停留 → 缓慢淡出） -->
  <div class="intro-title" v-if="phase === 'title' || phase === 'title-out'">
    <div class="intro-chars">
      <span
        v-for="(ch, i) in titleChars"
        :key="'it' + i"
        class="intro-char"
        :style="introCharStyle(i)"
      >{{ ch }}</span>
    </div>
    <div class="intro-sub">WINDOW OF THE WORLD</div>
  </div>

  <!-- ② 蛇形卡片墙：多条蛇在屏幕高度方向平行排布，蛇身卡片等距排开互不重叠，
       蛇 A/C/E/G 从左爬到右，蛇 B/D/F/H 从右爬到左，平行穿屏铺满整个屏幕 -->
  <transition name="wall-fade">
    <div class="snake-wall" v-if="phase === 'cards'">
      <div
        class="snake"
        v-for="(snake, si) in snakeLines"
        :key="'sn' + si"
        :class="'snk-' + (si % 2)"
        :style="snakeStyle(si)"
      >
        <div
          v-for="(img, ci) in snake.cards"
          :key="'sc' + si + '-' + ci"
          class="snake-card"
        >
          <img :src="img.thumb" :alt="img.name" draggable="false" decoding="async" />
        </div>
      </div>
    </div>
  </transition>

  <!-- ③ 视图窗口 -->
  <transition name="view-fade">
    <div class="view-window" v-if="phase === 'view'">
      <!-- 时段环境光 -->
      <div class="period-ambient" :class="period ? 'pa-' + period : ''"></div>

      <!-- 全页天气效果层（作用范围：整个页面） -->
      <transition name="wx-fade">
        <div class="wx wx-sunny" v-if="weather === 'sunny'">
          <div class="sun-wrap">
            <div class="sun-rays"></div>
            <div class="sun-disc"></div>
          </div>
        </div>
      </transition>
      <transition name="wx-fade">
        <div class="wx wx-rain" v-if="weather === 'rain'">
          <span class="drop" v-for="(d, di) in rainDrops" :key="'r' + di" :style="dropStyle(d)"></span>
        </div>
      </transition>
      <transition name="wx-fade">
        <div class="wx wx-fog" v-if="weather === 'fog'">
          <span class="fog-band"></span>
          <span class="fog-band"></span>
          <span class="fog-band"></span>
        </div>
      </transition>
      <transition name="wx-fade">
        <div class="wx wx-snow" v-if="weather === 'snow'">
          <span class="flake" v-for="(f, fi) in snowFlakes" :key="'s' + fi" :style="flakeStyle(f)"></span>
        </div>
      </transition>

      <!-- 顶部：艺术标题 -->
      <div class="view-title">
        <div class="view-title-chars">
          <span
            v-for="(ch, i) in titleChars"
            :key="'vt' + i"
            class="art-char"
            :style="artCharStyle(i)"
          >{{ ch }}</span>
        </div>
        <div class="view-title-en">WINDOW OF THE WORLD</div>

        <!-- 壁纸源切换：必应壁纸（默认，图+标题+介绍）/ Picsum 图库 / 本地画廊 -->
        <div class="source-switch">
          <button
            class="source-btn"
            :class="{ active: sourceMode === 'bing' }"
            @click="switchSource('bing')"
          >
            <i class="src-dot"></i>必应壁纸
          </button>
          <button
            class="source-btn"
            :class="{ active: sourceMode === 'picsum' }"
            @click="switchSource('picsum')"
          >
            <i class="src-dot"></i>Picsum 图库
          </button>
          <button
            class="source-btn"
            :class="{ active: sourceMode === 'local' }"
            @click="switchSource('local')"
          >
            <i class="src-dot"></i>本地画廊
          </button>
          <span v-if="bingLoading" class="src-loading">加载中…</span>
        </div>
      </div>

      <!-- 中部：左中右三栏 -->
      <div class="view-mid">
        <!-- 左栏：早晨 / 中午 / 傍晚 / 深夜 时段切换 -->
        <div class="side-panel left-panel">
          <div class="panel-title">时 段</div>
          <button
            v-for="p in periods"
            :key="p.key"
            class="side-btn"
            :class="{ active: period === p.key }"
            :title="p.label"
            @click="setPeriod(p.key)"
          >
            <svg class="side-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <template v-if="p.key === 'morning'">
                <path d="M4 17h16" />
                <path d="M7 17a5 5 0 0 1 10 0" />
                <path d="M12 5v2.2" />
                <path d="M6.4 7.6l1.5 1.5" />
                <path d="M17.6 7.6l-1.5 1.5" />
              </template>
              <template v-else-if="p.key === 'noon'">
                <circle cx="12" cy="12" r="4.2" />
                <path d="M12 3v2.4" />
                <path d="M12 18.6V21" />
                <path d="M3 12h2.4" />
                <path d="M18.6 12H21" />
                <path d="M6.2 6.2l1.7 1.7" />
                <path d="M16.1 16.1l1.7 1.7" />
                <path d="M17.8 6.2l-1.7 1.7" />
                <path d="M7.9 16.1l-1.7 1.7" />
              </template>
              <template v-else-if="p.key === 'dusk'">
                <path d="M4 17h16" />
                <path d="M8 17a4 4 0 0 1 8 0" />
                <path d="M12 6v2" />
                <path d="M7.2 9l1.4 1.4" />
                <path d="M16.8 9l-1.4 1.4" />
              </template>
              <template v-else>
                <path d="M19.5 14.6A8.2 8.2 0 1 1 9.4 4.5a6.4 6.4 0 0 0 10.1 10.1z" />
                <path d="M16.6 5.2l.5 1.3 1.3.5-1.3.5-.5 1.3-.5-1.3-1.3-.5 1.3-.5z" fill="currentColor" stroke="none" />
                <circle cx="18.4" cy="9.8" r="0.7" />
              </template>
            </svg>
            <span class="side-label">{{ p.label }}</span>
          </button>
        </div>

        <!-- 中栏：艺术级轮播浏览区 -->
        <div class="center-stage" :data-period="period" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
          <!-- 多排重叠交替装饰卡 -->
          <div class="collage">
            <div
              v-for="c in collageCards"
              :key="'ov' + c.idx"
              class="ov-card"
              :class="c.cls"
              :style="{ '--ov-rot': c.rot + 'deg' }"
            >
              <img :src="images[c.idx].thumb" :alt="images[c.idx].name" draggable="false" />
            </div>
          </div>

          <button class="nav-arrow prev" @click="prev" aria-label="上一张">
            <svg viewBox="0 0 24 24" width="26" height="26">
              <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <div class="frame-wrap" :class="{ 'frame-landscape': sourceMode !== 'local' }" @click="next">
            <div class="frame-glow"></div>
            <div class="frame-border">
              <span class="corner tl"></span>
              <span class="corner tr"></span>
              <span class="corner bl"></span>
              <span class="corner br"></span>

              <!-- 3D 翻转图片（非当前页用缩略图占位，当前/前一页加载原图，控制内存） -->
              <div class="card-3d" :style="cardStyle">
                <div
                  v-for="(img, i) in images"
                  :key="'f' + i"
                  class="card-face"
                  :class="{ active: i === current, prev: i === prevIndex }"
                >
                  <img :src="i === current || i === prevIndex ? img.src : img.thumb" :alt="img.name" draggable="false" />
                  <div class="card-overlay"></div>
                </div>
              </div>

              <!-- 时段光效层 -->
              <div
                class="time-layer"
                v-for="p in periods"
                :key="'tl' + p.key"
                :class="'tl-' + p.key"
                :style="{ opacity: period === p.key ? 1 : 0 }"
              ></div>

              <div class="flash" :class="{ on: flashing }"></div>
            </div>

            <!-- 在线图库模式：艺术之窗风格文案（眉题 + 渐变书法标题 + 介绍，依次浮现） -->
            <div
              v-if="sourceMode !== 'local'"
              class="bing-caption"
              :key="'cap-' + current"
            >
              <div class="cap-eyebrow">
                <span class="cap-num">{{ String(current + 1).padStart(2, '0') }}</span>
                <span class="cap-line"></span>
                <span class="cap-en">{{ sourceMode === 'picsum' ? 'PICSUM PHOTOS · RANDOM' : 'BING WALLPAPER · DAILY' }}</span>
              </div>
              <h2 class="cap-title">{{ images[current].name }}</h2>
              <p v-if="images[current].desc" class="cap-desc">{{ images[current].desc }}</p>
            </div>

            <!-- 本地模式：底部信息（序号 / 名称） -->
            <div v-if="sourceMode === 'local'" class="frame-info">
              <div class="info-line">
                <span class="info-index">{{ String(current + 1).padStart(2, '0') }}</span>
                <span class="info-sep">/</span>
                <span class="info-total">{{ String(images.length).padStart(2, '0') }}</span>
                <span class="info-name">{{ images[current].name }}</span>
              </div>
            </div>
          </div>

          <button class="nav-arrow next" @click="next" aria-label="下一张">
            <svg viewBox="0 0 24 24" width="26" height="26">
              <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- 右栏：晴 / 雨 / 雾 / 雪 天气切换 -->
        <div class="side-panel right-panel">
          <div class="panel-title">天 气</div>
          <button
            v-for="w in weathers"
            :key="w.key"
            class="side-btn"
            :class="{ active: weather === w.key }"
            :title="w.label"
            @click="setWeather(w.key)"
          >
            <svg class="side-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <template v-if="w.key === 'sunny'">
                <circle cx="12" cy="12" r="4.2" />
                <path d="M12 3v2.4" />
                <path d="M12 18.6V21" />
                <path d="M3 12h2.4" />
                <path d="M18.6 12H21" />
                <path d="M6.2 6.2l1.7 1.7" />
                <path d="M16.1 16.1l1.7 1.7" />
                <path d="M17.8 6.2l-1.7 1.7" />
                <path d="M7.9 16.1l-1.7 1.7" />
              </template>
              <template v-else-if="w.key === 'rain'">
                <path d="M17.5 14.5a4.5 4.5 0 0 0 .6-8.96 6 6 0 0 0-11.4 1.3A3.8 3.8 0 0 0 5.6 14.5h11.9z" />
                <path d="M8.5 18.5v1.8" />
                <path d="M12.5 18.5v2.2" />
                <path d="M16.5 18.5v1.8" />
              </template>
              <template v-else-if="w.key === 'fog'">
                <path d="M3 9h14" />
                <path d="M5 13h17" />
                <path d="M4 17h13" />
              </template>
              <template v-else>
                <path d="M17.5 14.5a4.5 4.5 0 0 0 .6-8.96 6 6 0 0 0-11.4 1.3A3.8 3.8 0 0 0 5.6 14.5h11.9z" />
                <circle cx="9.5" cy="19" r="0.8" />
                <circle cx="13.5" cy="19.6" r="0.8" />
                <circle cx="17.2" cy="18.8" r="0.8" />
              </template>
            </svg>
            <span class="side-label">{{ w.label }}</span>
          </button>
        </div>
      </div>

      <!-- 底部：小图预览导航（鼠标按住拖拽滑动，无滚动条） -->
      <div
        class="view-thumbs"
        :class="{ dragging: thumbDrag.down }"
        @mousedown="onThumbDown"
        @mousemove="onThumbMove"
        @mouseup="onThumbUp"
        @mouseleave="onThumbUp"
      >
        <div
          v-for="(img, i) in images"
          :key="'t' + i"
          class="thumb"
          :class="{ active: i === current }"
          @click="onThumbClick(i)"
        >
          <img :src="img.thumb" :alt="img.name" draggable="false" loading="lazy" />
          <span class="thumb-num">{{ i + 1 }}</span>
        </div>
      </div>

      <!-- 必应壁纸加载失败提示 -->
      <transition name="wx-fade">
        <div v-if="bingError" class="bing-error">{{ bingError }}</div>
      </transition>

      <!-- 进度线 -->
      <div class="progress-line">
        <div class="progress-fill" :style="{ width: progressWidth }"></div>
      </div>
    </div>
  </transition>
  </div>
</div>
</template>

<script>
import HeaderNav from "@/components/HeaderNav.vue";

export default {
  name: 'World',
  components: { HeaderNav },
  data() {
    return {
      /* —— 开场三阶段：title → cards → view —— */
      phase: 'title',        // title | title-out | cards | view

      /* —— 缩略图拖拽 —— */
      thumbDrag: { down: false, startX: 0, startLeft: 0, moved: false },

      /* —— 轮播 —— */
      current: 0,
      prevIndex: -1,
      direction: 1,
      flashing: false,
      animating: false,
      images: [],
      autoTimer: null,

      /* —— 壁纸源：bing 必应壁纸（默认） / picsum 随机图库 / local 本地画廊 —— */
      sourceMode: 'bing',
      localImages: [],
      bingImages: [],
      picsumImages: [],
      bingLoading: false,
      bingError: '',

      /* —— 时段 / 天气（默认均不开启，点击左侧栏/右侧栏按钮才生效） —— */
      period: null,
      weather: null,
      rainDrops: [],
      snowFlakes: [],

      /* —— 常量数据 —— */
      titleChars: ['世', '界', '之', '窗'],
      periods: [
        { key: 'morning', label: '早晨' },
        { key: 'noon', label: '中午' },
        { key: 'dusk', label: '傍晚' },
        { key: 'night', label: '深夜' }
      ],
      weathers: [
        { key: 'sunny', label: '晴' },
        { key: 'rain', label: '雨' },
        { key: 'fog', label: '雾' },
        { key: 'snow', label: '雪' }
      ],

      particles: []
    }
  },
  created() {
    this.rafId = null
    this.bgCtx = null
    this.bgW = 0
    this.bgH = 0
    this.introTimers = []
  },
  computed: {
    cardStyle() {
      return {
        transform: 'rotateY(' + (this.direction * -8) + 'deg)'
      }
    },
    progressWidth() {
      return ((this.current + 1) / this.images.length * 100) + '%'
    },
    // 轮播主图四周多排重叠交替的相邻图片
    collageCards() {
      const n = this.images.length
      if (!n) return []
      const wrap = (k) => ((k % n) + n) % n
      return [
        { idx: wrap(this.current - 2), cls: 'ov-a', rot: -12 },
        { idx: wrap(this.current - 1), cls: 'ov-b', rot: 9 },
        { idx: wrap(this.current + 1), cls: 'ov-c', rot: 12 },
        { idx: wrap(this.current + 2), cls: 'ov-d', rot: -9 }
      ]
    },
    // 蛇形卡片墙：固定使用本地画廊图片铺满 6 条蛇（本地图量大，Bing/Picsum 图少不够穿屏）
    snakeLines() {
      const pool = this.localImages
      const n = pool.length
      if (!n) return []
      const count = 6
      const lines = []
      let start = 0
      for (let s = 0; s < count; s++) {
        const size = Math.ceil((n - start) / (count - s))
        let cards = pool.slice(start, start + size)
        start += size
        if (s % 2 === 1) cards = [...cards].reverse()   // 右→左的蛇：蛇头（第一张）排在最右先入屏
        lines.push({ idx: s, cards })
      }
      return lines
    }
  },
  mounted() {
    // 本地画廊同步加载（兜底数据，保证开场动画期间有内容），Bing 壁纸异步加载（默认数据源），Picsum 图库同步生成
    this.loadLocalImages()
    this.loadPicsumImages()
    this.loadBingImages()

    this.$nextTick(() => {
      this.initCanvas()
      this.makeWeatherParticles()
      window.addEventListener('keydown', this.onKey)
      window.addEventListener('resize', this.onResize)
    })

    this.playIntro()
  },
  beforeDestroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('resize', this.onResize)
    this.introTimers.forEach(t => clearTimeout(t))
    this.clearAuto()
  },
  methods: {
    /* ============ 开场动画编排 ============ */
    playIntro() {
      // 0s：标题淡入（CSS 动画，字符高低错落依次浮现）
      // 2.2s：标题开始缓慢淡出（CSS animation delay 控制，1.1s 内淡完）
      this.introTimers.push(setTimeout(() => {
        this.phase = 'title-out'
      }, 2200))
      // 3.4s：标题已淡出，6 条蛇开始平行穿屏
      this.introTimers.push(setTimeout(() => {
        this.phase = 'cards'
      }, 3400))
      // ~6.6s：蛇动画 3s 结束（3.4s + 3s = 6.4s 全部出屏），随即进入视图窗口，不留空屏
      this.introTimers.push(setTimeout(() => {
        this.phase = 'view'
        this.startAuto()
      }, 6600))
    },

    /* ============ 图片加载（三数据源：本地画廊 / 必应壁纸 / Picsum 图库） ============ */
    // 本地画廊：仅使用本组件原有章节图（world-img-processed / world-img-thumbs）
    loadLocalImages() {
      const full = require.context('./world-img-processed', false, /ch\d+\.png$/)
      const thumb = require.context('./world-img-thumbs', false, /ch\d+\.jpg$/)
      const numOf = (key) => parseInt((key.match(/ch(\d+)/) || [])[1], 10)

      const fullMap = {}
      full.keys().forEach((key) => { fullMap[numOf(key)] = full(key) })
      const thumbMap = {}
      thumb.keys().forEach((key) => { thumbMap[numOf(key)] = thumb(key) })

      const list = Object.keys(fullMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map((n) => ({
          src: fullMap[n],
          thumb: thumbMap[n] || fullMap[n],
          name: '风景 · ' + n,
          desc: '',
          link: ''
        }))

      this.localImages = list
      // Bing 未就绪且未切到其它在线源时，以本地数据兜底渲染
      if (this.sourceMode === 'local' || (!this.bingImages.length && this.sourceMode === 'bing')) {
        this.applyImages(list)
      }
    },
    // 必应每日壁纸：图片 + 标题 + 介绍（走 devServer / nginx 代理解决 CORS）
    // Bing 单次最多返回 8 张，分 4 批（idx 0/8/16/24）拉取最近约 32 天壁纸并去重合并
    loadBingImages() {
      this.bingLoading = true
      this.bingError = ''
      const pages = [0, 8, 16, 24].map((idx) =>
        this.$axios.get('/bing-api/HPImageArchive.aspx', {
          params: { format: 'js', idx, n: 8, mkt: 'zh-CN' }
        }).then((res) => (res.data && res.data.images) || []).catch(() => [])
      )
      Promise.all(pages).then((groups) => {
        const seen = new Set()
        const raw = []
        groups.forEach((group) => group.forEach((item) => {
          const key = item.urlbase || item.url
          if (!seen.has(key)) { seen.add(key); raw.push(item) }
        }))
        const list = raw.map((item) => ({
          src: 'https://cn.bing.com' + item.url,
          thumb: 'https://cn.bing.com' + item.urlbase + '_640x360.jpg',
          name: item.title || item.copyright || '必应每日壁纸',
          desc: item.copyright || '',
          link: item.copyrightlink || ''
        }))
        if (!list.length) throw new Error('empty')
        this.bingImages = list
        if (this.sourceMode === 'bing') {
          this.applyImages(list)
        }
      }).catch(() => {
        this.bingError = '必应壁纸加载失败，已自动切换为本地画廊'
        if (this.sourceMode === 'bing') {
          this.sourceMode = 'local'
          this.applyImages(this.localImages || [])
        }
      }).finally(() => {
        this.bingLoading = false
      })
    },
    // Picsum 随机摄影图库：seed 固定生成 60 张横屏图（免 key、稳定不闪变）
    loadPicsumImages() {
      const list = []
      for (let i = 1; i <= 60; i++) {
        const seed = 'pink-dress-' + String(i).padStart(2, '0')
        list.push({
          src: 'https://picsum.photos/seed/' + seed + '/1920/1080',
          thumb: 'https://picsum.photos/seed/' + seed + '/640/360',
          name: '随机摄影 · ' + String(i).padStart(2, '0'),
          desc: 'Picsum Photos · 免版权高清摄影图库',
          link: ''
        })
      }
      this.picsumImages = list
    },
    // 应用一组图片作为轮播数据（重置进度与动画状态）
    applyImages(list) {
      this.current = 0
      this.prevIndex = -1
      this.animating = false
      this.images = list
    },
    // 手动切换壁纸源：bing 必应壁纸 / picsum 随机图库 / local 本地画廊
    switchSource(mode) {
      if (mode === this.sourceMode) return
      this.sourceMode = mode
      this.bingError = ''
      if (mode === 'bing') {
        if (this.bingImages.length) {
          this.applyImages(this.bingImages)
        } else {
          this.loadBingImages()
        }
      } else if (mode === 'picsum') {
        if (!this.picsumImages.length) {
          this.loadPicsumImages()
        }
        this.applyImages(this.picsumImages)
      } else {
        this.applyImages(this.localImages || [])
      }
    },

    /* ============ 蛇形卡片墙 ============ */
    snakeStyle(si) {
      // 6 条蛇铺满屏幕：蛇顶边 = 0.5vh + si × 15.7vh（卡片高 15vh、间隙 0.7vh ≈ 7px，紧凑不溢出）
      const y = (0.5 + si * 15.7).toFixed(1)
      // 6 条蛇同时出现、同时滑动，无需错开延迟（--sd 缺省 0s）
      return {
        top: y + 'vh'
      }
    },

    /* ============ 艺术文字 ============ */
    introCharStyle(i) {
      const off = [0, -0.24, 0.14, -0.1]      // 高低错落
      const scale = [1, 1.12, 0.94, 1.08]
      const delay = [0.15, 0.32, 0.5, 0.68]
      return {
        transform: 'translateY(' + off[i] + 'em) scale(' + scale[i] + ')',
        animationDelay: delay[i] + 's'
      }
    },
    artCharStyle(i) {
      const off = [0, -0.22, 0.13, -0.09]
      const size = [1, 1.12, 0.96, 1.06]
      return {
        transform: 'translateY(' + off[i] + 'em)',
        fontSize: size[i] + 'em'
      }
    },

    /* ============ 轮播 ============ */
    next() {
      if (this.animating) return
      this.direction = 1
      this.prevIndex = this.current
      this.current = (this.current + 1) % this.images.length
      this.triggerTransition()
    },
    prev() {
      if (this.animating) return
      this.direction = -1
      this.prevIndex = this.current
      this.current = (this.current - 1 + this.images.length) % this.images.length
      this.triggerTransition()
    },
    goTo(i) {
      if (this.animating || i === this.current) return
      this.direction = i > this.current ? 1 : -1
      this.prevIndex = this.current
      this.current = i
      this.triggerTransition()
    },

    /* ============ 缩略图条：鼠标拖拽滑动（无滚动条） ============ */
    onThumbDown(e) {
      this.thumbDrag.down = true
      this.thumbDrag.startX = e.clientX
      this.thumbDrag.startLeft = e.currentTarget.scrollLeft
      this.thumbDrag.moved = false
    },
    onThumbMove(e) {
      if (!this.thumbDrag.down) return
      const dx = e.clientX - this.thumbDrag.startX
      if (Math.abs(dx) > 6) this.thumbDrag.moved = true
      e.currentTarget.scrollLeft = this.thumbDrag.startLeft - dx
    },
    onThumbUp() {
      this.thumbDrag.down = false
      // 延迟复位 moved，避免松手瞬间的 click 被误判
      setTimeout(() => { this.thumbDrag.moved = false }, 50)
    },
    onThumbClick(i) {
      if (this.thumbDrag.moved) return
      this.goTo(i)
    },
    triggerTransition() {
      this.animating = true
      this.flashing = true
      setTimeout(() => { this.flashing = false }, 400)
      setTimeout(() => { this.animating = false }, 900)
    },
    startAuto() {
      this.clearAuto()
      this.autoTimer = setInterval(() => { this.next() }, 6000)
    },
    pauseAuto() {
      this.clearAuto()
    },
    resumeAuto() {
      this.startAuto()
    },
    clearAuto() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer)
        this.autoTimer = null
      }
    },

    /* ============ 时段 / 天气 ============ */
    setPeriod(key) {
      // 再次点击同一时段则取消效果
      this.period = this.period === key ? null : key
    },
    setWeather(key) {
      // 再次点击同一天气则取消效果
      if (this.weather === key) {
        this.weather = null
      } else {
        this.weather = key
        this.makeWeatherParticles()
      }
    },
    makeWeatherParticles() {
      this.rainDrops = Array.from({ length: 60 }, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 2.4,
        dur: 0.6 + Math.random() * 1.0,
        h: 40 + Math.random() * 60,
        o: 0.35 + Math.random() * 0.45,
        ang: (8 + Math.random() * 14).toFixed(1),
        wd: Math.round(Math.random() * 90 - 35)
      }))
      this.snowFlakes = Array.from({ length: 30 }, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 4,
        dur: 5 + Math.random() * 6,
        size: 3 + Math.random() * 5,
        sway: Math.round(Math.random() * 90 - 45)
      }))
    },
    dropStyle(d) {
      return {
        left: d.left + '%',
        height: d.h + 'px',
        animationDelay: d.delay + 's',
        animationDuration: d.dur + 's',
        '--o': d.o,
        '--ang': d.ang + 'deg',
        '--wd': d.wd + 'px'
      }
    },
    flakeStyle(f) {
      return {
        left: f.left + '%',
        width: f.size + 'px',
        height: f.size + 'px',
        animationDelay: f.delay + 's',
        animationDuration: f.dur + 's',
        '--sway': f.sway + 'px'
      }
    },

    /* ============ 键盘 / 尺寸 ============ */
    onKey(e) {
      if (e.key === 'ArrowRight' || e.key === ' ') this.next()
      if (e.key === 'ArrowLeft') this.prev()
    },
    onResize() {
      this.initCanvas()
    },

    /* ============ 粒子背景 ============ */
    initCanvas() {
      const canvas = this.$refs.bgCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      const dpr = window.devicePixelRatio || 1
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.scale(dpr, dpr)
      this.bgCtx = ctx
      this.bgW = w
      this.bgH = h

      const count = Math.min(90, Math.floor(w * h / 18000))
      this.particles = []
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 0.6 + Math.random() * 2.2,
          vx: (Math.random() - 0.5) * 0.25,
          vy: -0.1 - Math.random() * 0.35,
          alpha: 0.15 + Math.random() * 0.5,
          phase: Math.random() * Math.PI * 2,
          speed: 0.005 + Math.random() * 0.012,
          hue: Math.random() < 0.6 ? 330 : (Math.random() < 0.5 ? 280 : 190)
        })
      }
      if (!this.rafId) this.animateBg()
    },
    animateBg() {
      const ctx = this.bgCtx
      const w = this.bgW
      const h = this.bgH
      ctx.clearRect(0, 0, w, h)

      for (const p of this.particles) {
        p.x += p.vx + Math.sin(p.phase) * 0.15
        p.y += p.vy
        p.phase += p.speed
        if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w }
        if (p.x < -10) p.x = w + 10
        if (p.x > w + 10) p.x = -10

        const flicker = 0.7 + 0.3 * Math.sin(p.phase * 2)
        ctx.globalCompositeOperation = 'lighter'
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4)
        grad.addColorStop(0, 'hsla(' + p.hue + ', 90%, 75%, ' + (p.alpha * flicker) + ')')
        grad.addColorStop(1, 'hsla(' + p.hue + ', 90%, 75%, 0)')
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2)
        ctx.fill()
      }

      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 1
      this.rafId = requestAnimationFrame(this.animateBg)
    }
  }
}
</script>

<style>
/* 毛笔书法标题字体（与艺术之窗共用，-fixed 版本可正确解析避免 OTS 报错） */
@font-face {
  font-family: 'MoFa-ShuFa';
  src: url('~@/assets/fonts/书法毛笔-fixed.ttf') format('truetype');
  font-display: swap;
}
</style>

<style scoped>
.world {
  position: fixed;
  inset: 0;
  padding-top: 60px;
  overflow: hidden;
  background: radial-gradient(ellipse at 50% 30%, #2a1535 0%, #160a22 50%, #0a0512 100%);
  font-family: 'Noto Serif SC', 'Songti SC', serif;
}

/* ===== 粒子 canvas ===== */
.world-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

/* ===== 氛围光斑 ===== */
.ambient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.orb1 {
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(255,120,200,0.12), transparent 70%);
  top: -100px; left: -100px;
  animation: orbFloat1 18s ease-in-out infinite;
}
.orb2 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(123,63,242,0.13), transparent 70%);
  bottom: -150px; right: -100px;
  animation: orbFloat2 22s ease-in-out infinite;
}
.orb3 {
  width: 350px; height: 350px;
  background: radial-gradient(circle, rgba(255,180,100,0.07), transparent 70%);
  top: 40%; left: 50%;
  transform: translateX(-50%);
  animation: orbFloat3 16s ease-in-out infinite;
}
@keyframes orbFloat1 {
  0%,100% { transform: translate(0,0); }
  50% { transform: translate(60px,40px); }
}
@keyframes orbFloat2 {
  0%,100% { transform: translate(0,0); }
  50% { transform: translate(-50px,-60px); }
}
@keyframes orbFloat3 {
  0%,100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-40px); }
}

/* =========================================================
   ① 开场艺术标题
   ========================================================= */
.intro-title {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  background: radial-gradient(ellipse at 50% 42%, rgba(42,18,58,.96) 0%, rgba(12,5,22,.97) 78%);
  pointer-events: none;
  user-select: none;
  opacity: 0;
  animation:
    introIn 1.3s cubic-bezier(.16,1,.3,1) forwards,
    introOut 1.1s ease 2.2s forwards;
}
@keyframes introIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes introOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
.intro-chars {
  display: flex;
  align-items: baseline;
  letter-spacing: .16em;
  margin-right: -.16em;
}
.intro-char {
  display: inline-block;
  font-size: clamp(72px, 15vh, 172px);
  font-weight: 300;
  line-height: 1;
  background: linear-gradient(160deg, #ffe4f5 0%, #ff9ed8 34%, #c98bff 68%, #8fb6ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  animation: charIn 1.1s cubic-bezier(.2,1.1,.3,1) forwards;
}
@keyframes charIn {
  from { opacity: 0; filter: blur(16px); }
  to { opacity: 1; filter: blur(0) drop-shadow(0 0 26px rgba(255,150,220,.5)); }
}
.intro-sub {
  font-family: 'Georgia', serif;
  font-size: 14px;
  letter-spacing: .7em;
  margin-right: -.7em;
  color: rgba(255,200,240,.55);
  opacity: 0;
  animation: subIn 1s ease .5s forwards;
}
@keyframes subIn {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

/* =========================================================
   ② 蛇形卡片墙（平行穿屏阶段）
   ========================================================= */
.snake-wall {
  position: fixed;
  left: 0; right: 0; top: 60px; bottom: 0;
  z-index: 4;
  pointer-events: none;
  overflow: hidden;
}
.snake {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  will-change: transform;
}
.snake-card {
  flex: 0 0 auto;
  width: 170px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255,200,240,.18);
  box-shadow: 0 8px 22px rgba(0,0,0,.45);
  background: #190d28;
}
.snake-card + .snake-card {
  margin-left: 8px;      /* 蛇身卡片等距排开，互不重叠 */
}
.snake-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
/* 动画经 scoped 类引用（keyframes 会被 vue-loader 自动加哈希后缀，类内引用同步生效） */
.snake.snk-0 { animation: snakeLR 4.5s linear var(--sd, 0s) both; }   /* 左 → 右 */
.snake.snk-1 { animation: snakeRL 4.5s linear var(--sd, 0s) both; }   /* 右 → 左 */

/* 蛇 LR：从屏幕左外平移到右外 */
@keyframes snakeLR {
  0%   { transform: translate(-250vw, 0); }
  100% { transform: translate(250vw, 0); }
}
/* 蛇 RL：从屏幕右外平移到左外（与 LR 对称，蛇身完整穿越） */
@keyframes snakeRL {
  0%   { transform: translate(250vw, 0); }
  100% { transform: translate(-250vw, 0); }
}
.wall-fade-leave-active {
  transition: opacity .75s ease, transform .9s ease;
}
.wall-fade-leave-to {
  opacity: 0;
  transform: scale(1.04);
}

/* =========================================================
   ③ 视图窗口
   ========================================================= */
.view-fade-enter-active {
  transition: opacity .9s ease .1s, transform .9s ease .1s;
}
.view-fade-enter {
  opacity: 0;
  transform: translateY(18px);
}

.view-window {
  position: fixed;
  left: 0; right: 0; top: 60px; bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 18px 12px;
  box-sizing: border-box;
}

/* 时段环境光 */
.period-ambient {
  position: fixed;
  inset: 60px 0 0;
  z-index: 2;
  pointer-events: none;
  transition: background 1.2s ease;
}
.pa-morning {
  background: radial-gradient(ellipse at 50% 0%, rgba(255,180,120,.10), transparent 60%);
}
.pa-noon {
  background: radial-gradient(ellipse at 50% 0%, rgba(255,250,210,.07), transparent 60%);
}
.pa-dusk {
  background: radial-gradient(ellipse at 50% 0%, rgba(255,110,90,.10), rgba(120,60,180,.05) 55%, transparent 75%);
}
.pa-night {
  background: radial-gradient(ellipse at 50% 0%, rgba(30,40,120,.22), rgba(10,8,40,.25) 70%);
}

/* —— 顶部艺术标题 —— */
.view-title {
  position: relative;
  z-index: 3;
  text-align: center;
  flex-shrink: 0;
  padding-top: 2px;
}
.view-title-chars {
  display: flex;
  align-items: baseline;
  justify-content: center;
  letter-spacing: .34em;
  margin-right: -.34em;
}
.art-char {
  display: inline-block;
  font-size: clamp(24px, 3.6vh, 38px);
  font-weight: 300;
  line-height: 1;
  background: linear-gradient(160deg, #ffe4f5 0%, #ff9ed8 34%, #c98bff 68%, #8fb6ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 14px rgba(255,150,220,.35));
  user-select: none;
}
.view-title-en {
  font-family: 'Georgia', serif;
  font-size: 9px;
  letter-spacing: .55em;
  margin-right: -.55em;
  margin-top: 5px;
  color: rgba(255,200,240,.45);
  user-select: none;
}

/* —— 壁纸源切换（必应壁纸 / 本地画廊） —— */
.source-switch {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
}
.source-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255,180,230,0.25);
  background: rgba(30,12,44,0.5);
  backdrop-filter: blur(10px);
  color: rgba(255,200,240,0.7);
  font-size: 12px;
  letter-spacing: 2px;
  font-family: 'Noto Serif SC', 'Songti SC', serif;
  cursor: pointer;
  transition: all .35s ease;
  outline: none;
}
.source-btn:hover {
  border-color: rgba(255,150,220,0.5);
  color: #ffcdf0;
}
.source-btn.active {
  border-color: rgba(255,150,220,0.85);
  color: #ffd3f1;
  background: rgba(255,120,200,0.16);
  box-shadow: 0 0 14px rgba(255,150,220,0.3);
}
.src-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255,150,220,0.35);
  transition: background .35s ease, box-shadow .35s ease;
}
.source-btn.active .src-dot {
  background: #ff8ad4;
  box-shadow: 0 0 8px rgba(255,138,212,0.9);
}
.src-loading {
  font-size: 11px;
  letter-spacing: 2px;
  color: rgba(255,200,240,0.5);
  animation: srcPulse 1.2s ease-in-out infinite;
}
@keyframes srcPulse {
  0%,100% { opacity: .4; }
  50% { opacity: 1; }
}

/* —— 中部三栏 —— */
.view-mid {
  position: relative;
  z-index: 3;
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
}

/* 左右栏 */
.side-panel {
  flex: 0 0 5%;
  min-width: 52px;
  max-width: 120px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 12px;
}
.panel-title {
  text-align: center;
  font-size: 11px;
  letter-spacing: 4px;
  color: rgba(255,200,240,.4);
  margin-bottom: 2px;
  user-select: none;
}
.side-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 9px 4px 8px;
  border-radius: 12px;
  border: 1px solid rgba(255,180,230,.14);
  background: rgba(30,12,44,.45);
  backdrop-filter: blur(8px);
  color: rgba(255,200,240,.62);
  cursor: pointer;
  transition: all .35s ease;
  outline: none;
}
.side-btn:hover {
  border-color: rgba(255,150,220,.45);
  color: #ffc9ec;
  background: rgba(255,120,200,.1);
}
.side-btn.active {
  border-color: rgba(255,150,220,.8);
  color: #ffcdf0;
  background: rgba(255,120,200,.16);
  box-shadow: 0 0 18px rgba(255,150,220,.35), inset 0 0 12px rgba(255,150,220,.12);
  transform: scale(1.05);
}
.side-icon {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}
.side-label {
  font-size: 12px;
  letter-spacing: 3px;
  font-family: 'Noto Serif SC', 'Songti SC', serif;
}

/* —— 中栏轮播 —— */
.center-stage {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
}
.center-stage[data-period="morning"] .card-face.active img { filter: brightness(1.03) saturate(1.06); }
.center-stage[data-period="noon"] .card-face.active img { filter: brightness(1.06) saturate(.97) contrast(.98); }
.center-stage[data-period="dusk"] .card-face.active img { filter: brightness(.92) saturate(1.12) sepia(.12); }
.center-stage[data-period="night"] .card-face.active img { filter: brightness(.66) saturate(1.2) hue-rotate(8deg); }

/* 魔法相框 */
.frame-wrap {
  position: relative;
  height: 100%;
  max-height: 750px;
  aspect-ratio: 3 / 4;
  cursor: pointer;
  perspective: 1400px;
  z-index: 5;
}
/* 必应横屏壁纸：中间区域全部用于横屏展示（宽度优先、高度自适应、不溢出视口） */
.frame-wrap.frame-landscape {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  height: auto;
  max-width: calc((100vh - 280px) * 16 / 9);
  max-height: none;
  aspect-ratio: 16 / 9;
}

/* —— 多排重叠交替装饰卡（围绕主图，翻页时随之交替） —— */
.collage {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}
.ov-card {
  position: absolute;
  width: 30%;
  max-width: 300px;
  aspect-ratio: 3 / 2;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255,200,240,.22);
  box-shadow: 0 12px 30px rgba(0,0,0,.5);
  background: #190d28;
  filter: brightness(.78) saturate(.92);
  transform: rotate(var(--ov-rot, 0deg));
  animation: ovSwap .6s cubic-bezier(.2,.9,.3,1) both;
}
.ov-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ov-card.ov-a { top: 4%; left: 1%; z-index: 2; }
.ov-card.ov-b { top: 4%; right: 1%; z-index: 3; }
.ov-card.ov-c { bottom: 5%; left: 1%; z-index: 3; }
.ov-card.ov-d { bottom: 5%; right: 1%; z-index: 2; }
@keyframes ovSwap {
  0% { opacity: 0; transform: rotate(var(--ov-rot, 0deg)) scale(.78); }
  100% { opacity: 1; transform: rotate(var(--ov-rot, 0deg)) scale(1); }
}
.frame-glow {
  position: absolute;
  inset: -20px;
  border-radius: 20px;
  background: linear-gradient(135deg,
    rgba(255,150,220,0.25),
    rgba(123,63,242,0.2),
    rgba(255,200,100,0.15),
    rgba(255,150,220,0.25)
  );
  background-size: 300% 300%;
  filter: blur(30px);
  animation: glowShift 6s ease infinite;
  z-index: 0;
}
@keyframes glowShift {
  0%,100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.frame-border {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(255,200,240,0.3);
  background: linear-gradient(145deg, rgba(40,20,55,0.9), rgba(20,10,30,0.95));
  box-shadow:
    0 0 0 1px rgba(255,180,230,0.1),
    0 20px 60px rgba(0,0,0,0.6),
    inset 0 0 40px rgba(255,150,220,0.05);
  overflow: hidden;
  z-index: 1;
}
.corner {
  position: absolute;
  width: 24px;
  height: 24px;
  z-index: 10;
  pointer-events: none;
}
.corner::before, .corner::after {
  content: '';
  position: absolute;
  background: linear-gradient(135deg, #ffb8e6, #c084fc);
  box-shadow: 0 0 8px rgba(255,150,220,0.6);
}
.corner::before { width: 24px; height: 1.5px; }
.corner::after { width: 1.5px; height: 24px; }
.corner.tl { top: 10px; left: 10px; }
.corner.tr { top: 10px; right: 10px; transform: scaleX(-1); }
.corner.bl { bottom: 10px; left: 10px; transform: scaleY(-1); }
.corner.br { bottom: 10px; right: 10px; transform: scale(-1,-1); }

.card-3d {
  position: absolute;
  inset: 8px;
  border-radius: 8px;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.card-face {
  position: absolute;
  inset: 0;
  border-radius: 8px;
  overflow: hidden;
  opacity: 0;
  transform: rotateY(90deg) scale(0.85);
  transition:
    opacity 0.5s ease,
    transform 0.85s cubic-bezier(0.34, 1.2, 0.4, 1),
    filter 0.6s ease;
  filter: blur(12px) brightness(0.5);
}
.card-face img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: filter 1.2s ease;
}
.card-face.active {
  opacity: 1;
  transform: rotateY(0deg) scale(1);
  filter: blur(0) brightness(1);
  z-index: 2;
}
.card-face.prev {
  opacity: 0;
  transform: rotateY(-90deg) scale(0.85);
  filter: blur(12px) brightness(0.5);
  z-index: 1;
}
.card-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, rgba(20,5,30,0.15) 0%, transparent 30%, transparent 65%, rgba(20,5,30,0.4) 100%),
    radial-gradient(ellipse at center, transparent 60%, rgba(10,0,20,0.3) 100%);
  pointer-events: none;
}
/* 切换闪光 */
.flash {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center,
    rgba(255,230,250,0.5) 0%,
    rgba(255,180,230,0.2) 40%,
    transparent 70%);
  opacity: 0;
  pointer-events: none;
  z-index: 20;
  border-radius: 8px;
}
.flash.on {
  animation: flashBlast 0.5s ease-out forwards;
}
@keyframes flashBlast {
  0% { opacity: 0; transform: scale(0.6); }
  30% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0; transform: scale(1.3); }
}

/* —— 必应模式：艺术之窗风格文案（眉题 + 渐变书法标题 + 介绍，切换时依次浮现） —— */
.bing-caption {
  position: absolute;
  left: clamp(18px, 4%, 42px);
  bottom: clamp(14px, 6%, 30px);
  max-width: min(560px, 80%);
  z-index: 14;
  color: #fff;
  pointer-events: none;
  text-shadow: 0 2px 26px rgba(20, 8, 40, 0.55);
}
.cap-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  animation: capUp .8s cubic-bezier(.16, 1, .3, 1) .15s both;
}
.cap-num {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 13px;
  letter-spacing: .14em;
  color: #ffd9ec;
}
.cap-line {
  width: 34px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 200, 230, .85), transparent);
}
.cap-en {
  font-size: 10px;
  letter-spacing: .3em;
  color: rgba(255, 236, 246, .75);
  text-transform: uppercase;
}
.cap-title {
  margin: 12px 0 6px;
  font-family: 'MoFa-ShuFa', 'STKaiti', 'KaiTi', 'Microsoft YaHei', serif;
  font-size: clamp(22px, 3vw, 40px);
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: .12em;
  background: linear-gradient(118deg, #ffffff 18%, #ffd9ec 46%, #e6d6ff 72%, #ffffff 92%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: capUp .9s cubic-bezier(.16, 1, .3, 1) .3s both;
}
.cap-desc {
  margin: 0;
  max-width: 520px;
  font-size: clamp(12px, 1.1vw, 15px);
  line-height: 1.8;
  letter-spacing: .05em;
  color: rgba(255, 255, 255, .88);
  animation: capUp .85s cubic-bezier(.16, 1, .3, 1) .48s both;
}
@keyframes capUp {
  from { opacity: 0; transform: translateY(18px); filter: blur(8px); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
}

/* 底部信息（叠在画面下方：序号 / 名称 + 图片介绍） */
.frame-info {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  max-width: 86%;
  z-index: 12;
  text-shadow: 0 1px 8px rgba(0,0,0,.6);
  pointer-events: none;
}
.info-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
}
.info-desc {
  margin: 0;
  max-width: 560px;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 1px;
  color: rgba(255,215,245,0.82);
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.info-index {
  font-size: 22px;
  font-weight: 300;
  color: #ffb8e6;
}
.info-sep, .info-total {
  font-size: 13px;
  color: rgba(255,215,245,.65);
}
.info-name {
  font-size: 12px;
  letter-spacing: 3px;
  color: rgba(255,215,245,.7);
  margin-left: 12px;
}

/* 导航箭头 */
.nav-arrow {
  position: relative;
  z-index: 6;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1px solid rgba(255,180,230,0.25);
  background: rgba(40,20,55,0.5);
  backdrop-filter: blur(10px);
  color: rgba(255,200,240,0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.35s ease;
  flex-shrink: 0;
  outline: none;
}
.nav-arrow:hover {
  border-color: rgba(255,150,220,0.6);
  background: rgba(255,120,200,0.12);
  color: #ffb8e6;
  box-shadow: 0 0 25px rgba(255,150,220,0.3);
  transform: scale(1.08);
}
.nav-arrow:active { transform: scale(0.95); }

/* —— 时段光效层 —— */
.time-layer {
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: none;
  transition: opacity 1.2s ease;
}
.tl-morning {
  background: linear-gradient(180deg, rgba(255,196,150,.25), rgba(255,150,200,.12) 45%, rgba(150,185,255,.10));
}
.tl-noon {
  background: linear-gradient(180deg, rgba(255,252,225,.16), rgba(190,225,255,.08));
}
.tl-dusk {
  background: linear-gradient(180deg, rgba(255,120,85,.30), rgba(160,80,210,.22) 55%, rgba(50,35,120,.30));
}
.tl-night {
  background: linear-gradient(180deg, rgba(12,22,68,.52), rgba(18,8,48,.58));
}

/* —— 天气效果层 —— */
.wx {
  position: fixed;
  inset: 60px 0 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 6;
}
.wx-fade-enter-active, .wx-fade-leave-active {
  transition: opacity .7s ease;
}
.wx-fade-enter, .wx-fade-leave-to {
  opacity: 0;
}

/* 晴：整体变亮（暖光提亮全页） */
.wx-sunny {
  background: rgba(255,250,220,.10);
  backdrop-filter: brightness(1.12) saturate(1.06) sepia(.03);
}
.sun-wrap {
  position: absolute;
  top: 4%;
  left: 3%;
  width: 30px;
  height: 30px;
  animation: sunPulse 5s ease-in-out infinite;
}
.sun-disc {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle, #fffbe8 0%, #ffe9a8 45%, rgba(255,220,140,0) 70%);
  box-shadow: 0 0 60px 22px rgba(255,224,150,.35);
}
.sun-rays {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: repeating-conic-gradient(from 0deg, rgba(255,230,160,.4) 0deg 10deg, transparent 10deg 24deg);
  -webkit-mask: radial-gradient(circle, transparent 30%, #000 33%);
  mask: radial-gradient(circle, transparent 30%, #000 33%);
  animation: sunSpin 26s linear infinite;
}
@keyframes sunSpin {
  to { transform: rotate(360deg); }
}
@keyframes sunPulse {
  0%,100% { transform: scale(1); opacity: .92; }
  50% { transform: scale(1.06); opacity: 1; }
}

/* 雨：流畅渐变雨丝，带倾斜与风向漂移，落点渐隐 */
.wx-rain .drop {
  position: absolute;
  top: -90px;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(205,222,255,.75) 45%, rgba(170,200,255,.32) 100%);
  transform: rotate(var(--ang, 18deg));
  animation: rainFall linear infinite;
  will-change: transform;
}
@keyframes rainFall {
  0% { transform: translate(0, -120px) rotate(var(--ang, 18deg)); opacity: 0; }
  10% { opacity: var(--o, .6); }
  80% { opacity: var(--o, .6); }
  100% { transform: translate(var(--wd, 0px), 1100px) rotate(var(--ang, 18deg)); opacity: 0; }
}

/* 雾：整体变朦胧（白色纱幕 + 模糊 + 雾带漂移） */
.wx-fog {
  background: rgba(228,234,246,.32);
  backdrop-filter: blur(6px) saturate(.72) brightness(1.05);
}
.wx-fog .fog-band {
  position: absolute;
  left: -20%;
  width: 140%;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(230,235,245,.3), transparent 65%);
  filter: blur(16px);
  animation: fogDrift 26s ease-in-out infinite alternate;
}
.wx-fog .fog-band:nth-child(1) { top: 6%; animation-duration: 22s; }
.wx-fog .fog-band:nth-child(2) { top: 38%; animation-duration: 30s; animation-delay: -8s; opacity: .85; }
.wx-fog .fog-band:nth-child(3) { top: 66%; animation-duration: 26s; animation-delay: -14s; }
@keyframes fogDrift {
  from { transform: translateX(-6%); }
  to { transform: translateX(6%); }
}

/* 雪 */
.wx-snow .flake {
  position: absolute;
  top: -20px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff, rgba(220,235,255,.85));
  box-shadow: 0 0 6px rgba(255,255,255,.7);
  animation: snowFall linear infinite;
}
@keyframes snowFall {
  0% { transform: translate(0, -60px); opacity: 0; }
  8% { opacity: .95; }
  100% { transform: translate(var(--sway), 1060px); opacity: .5; }
}

/* —— 底部缩略图导航 —— */
.view-thumbs {
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  display: flex;
  width: 70%;
  transform: translateX(20%);
  gap: 9px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(20,10,30,0.42);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,180,230,0.08);
  overflow-x: auto;
  overflow-y: hidden;
  cursor: grab;
  user-select: none;
  scrollbar-width: none;              /* 隐藏滚动条：Firefox */
  -ms-overflow-style: none;           /* IE / Edge 旧版 */
}
.view-thumbs.dragging {
  cursor: grabbing;
}
.view-thumbs::-webkit-scrollbar {
  display: none;                      /* 隐藏滚动条：Chrome / Edge / Safari */
}
.thumb {
  position: relative;
  width: 46px;
  height: 62px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 1.5px solid transparent;
  opacity: 0.5;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-drag: none;
  transition: all 0.4s cubic-bezier(0.34, 1.3, 0.4, 1);
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumb-num {
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 9px;
  color: rgba(255,255,255,0.75);
  text-shadow: 0 1px 3px rgba(0,0,0,0.8);
}
.thumb:hover {
  opacity: 0.85;
  transform: translateY(-3px);
}
.thumb.active {
  opacity: 1;
  border-color: rgba(255,150,220,0.7);
  box-shadow: 0 0 16px rgba(255,150,220,0.4), 0 4px 12px rgba(0,0,0,0.4);
  transform: translateY(-4px) scale(1.05);
}

/* —— 必应壁纸加载失败提示 —— */
.bing-error {
  position: fixed;
  left: 50%;
  bottom: 64px;
  transform: translateX(-50%);
  z-index: 20;
  padding: 8px 20px;
  border-radius: 999px;
  border: 1px solid rgba(255,150,180,0.3);
  background: rgba(60,18,38,0.72);
  backdrop-filter: blur(10px);
  color: #ffd0e4;
  font-size: 12px;
  letter-spacing: 1px;
  pointer-events: none;
}

/* —— 进度线 —— */
.progress-line {
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  height: 2px;
  background: rgba(255,180,230,0.08);
  border-radius: 2px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff8ad4, #c084fc, #ff8ad4);
  background-size: 200% 100%;
  box-shadow: 0 0 10px rgba(255,150,220,0.6);
  transition: width 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  animation: progressShimmer 3s linear infinite;
}
@keyframes progressShimmer {
  0% { background-position: 0% 0; }
  100% { background-position: 200% 0; }
}

/* ===== 响应式 ===== */
@media (max-width: 900px) {
  .view-mid { gap: 8px; }
  .side-panel { min-width: 44px; }
  .side-icon { width: 17px; height: 17px; }
  .side-label { font-size: 11px; letter-spacing: 1px; }
  .side-btn { padding: 7px 2px 6px; }
  .view-window { padding: 8px 10px 10px; }
  .nav-arrow { width: 40px; height: 40px; }
  .source-btn { padding: 5px 10px; font-size: 11px; letter-spacing: 1px; }
  .src-dot { width: 6px; height: 6px; }
}
@media (max-width: 640px) {
  .view-title-chars { letter-spacing: .18em; margin-right: -.18em; }
  .panel-title { display: none; }
  .side-btn .side-label { display: none; }
  .thumb { width: 36px; height: 48px; }
}
</style>
